// GENERATED BY portal-gen — DO NOT EDIT.
//
// Source: Policy — policy-admin.v1.yaml
// Regenerate with `npm run generate`; `npm run check` byte-diffs this file against a
// fresh run and fails the build on any difference.
//
// Every object below preserves the contract's `additionalProperties: false` as `.strict()`.
// That is a privacy control, not a typing convenience: a control-plane response that ever
// grows a content-bearing field must fail to parse, not render.

import { z } from "zod";

export const Action = z.enum(["voice", "text", "read_page", "copy", "web_search"]);
export type Action = z.infer<typeof Action>;

export const Caching = z.object({
  default: z.enum(["on", "off"]),
  ttlSec: z.number().int(),
}).strict();
export type Caching = z.infer<typeof Caching>;

export const Clarify = z.object({
  maxQuestions: z.number().int(),
}).strict();
export type Clarify = z.infer<typeof Clarify>;

export const Guard = z.object({
  mandatory: z.boolean(),
  reversible: z.boolean(),
}).strict();
export type Guard = z.infer<typeof Guard>;

export const Models = z.object({
  allowed: z.array(z.string()),
  default: z.string().nullable(),
  maxTokens: z.number().int().nullable(),
  temperature: z.number().nullable(),
}).strict();
export type Models = z.infer<typeof Models>;

export const Plan = z.enum(["individual", "team", "enterprise"]);
export type Plan = z.infer<typeof Plan>;

export const PolicyAuditEntry = z.object({
  org_id: z.string().uuid(),
  actor_sub_hash: z.string(),
  action: z.enum(["policy.updated", "policy.patched", "policy.reset", "policy.seeded", "policy.tier_rederived"]),
  from_version: z.number().int().nullable(),
  to_version: z.number().int(),
  changed_keys: z.array(z.string()),
  source: z.enum(["admin", "event"]).optional(),
  changed_at: z.string().datetime(),
}).strict();
export type PolicyAuditEntry = z.infer<typeof PolicyAuditEntry>;

/** `off` disables redaction for the org (the SKIP_REDACT posture, org-scoped); `standard`/`strict` map to the Redaction leaf strictness. `relaxed` is accepted for back-compat and normalizes to `standard` on the client projection. */
export const RedactionLevel = z.enum(["off", "standard", "strict"]);
export type RedactionLevel = z.infer<typeof RedactionLevel>;

export const RedactionPolicy = z.enum(["pii", "phi", "pci", "secrets"]);
export type RedactionPolicy = z.infer<typeof RedactionPolicy>;

export const Redaction = z.object({
  level: RedactionLevel,
  policies: z.array(RedactionPolicy),
}).strict();
export type Redaction = z.infer<typeof Redaction>;

export const Provider = z.enum(["groq", "together"]);
export type Provider = z.infer<typeof Provider>;

export const Providers = z.object({
  allowed: z.array(Provider),
}).strict();
export type Providers = z.infer<typeof Providers>;

export const SurfacePolicy = z.object({
  disabledActions: z.array(Action),
}).strict();
export type SurfacePolicy = z.infer<typeof SurfacePolicy>;

export const PolicyDocument = z.object({
  schema_version: z.number().int(),
  version: z.number().int(),
  org: z.string().uuid(),
  tier: Plan,
  redaction: Redaction,
  guard: Guard,
  models: Models,
  providers: Providers,
  actions: z.object({
    default: z.array(Action),
    standard: z.array(Action),
    premium: z.array(Action),
  }).strict(),
  surfaces: z.object({
    browser_ext: SurfacePolicy.optional(),
    public_api: SurfacePolicy.optional(),
    mcp: SurfacePolicy.optional(),
    vscode: SurfacePolicy.optional(),
    antigravity: SurfacePolicy.optional(),
    codex: SurfacePolicy.optional(),
  }).strict(),
  clarify: Clarify,
  caching: Caching,
  allowedDomains: z.array(z.string()),
  refreshIntervalSec: z.number().int(),
  degraded: z.boolean().optional(),
}).strict();
export type PolicyDocument = z.infer<typeof PolicyDocument>;

/** A partial `PolicyDocument` carrying ONLY the keys the admin intends to pin over the plan-tier defaults. Every top-level block is optional and independently validated. `schema_version`, `version`, `org`, `tier`, and `degraded` are server-owned and MUST NOT appear here. */
export const PolicyOverrides = z.object({
  redaction: Redaction.optional(),
  guard: Guard.optional(),
  models: Models.optional(),
  providers: Providers.optional(),
  actions: z.object({
    default: z.array(Action).optional(),
    standard: z.array(Action).optional(),
    premium: z.array(Action).optional(),
  }).strict().optional(),
  surfaces: z.object({
    browser_ext: SurfacePolicy.optional(),
    public_api: SurfacePolicy.optional(),
    mcp: SurfacePolicy.optional(),
    vscode: SurfacePolicy.optional(),
    antigravity: SurfacePolicy.optional(),
    codex: SurfacePolicy.optional(),
  }).strict().optional(),
  clarify: Clarify.optional(),
  caching: Caching.optional(),
  allowedDomains: z.array(z.string()).optional(),
  refreshIntervalSec: z.number().int().optional(),
}).strict();
export type PolicyOverrides = z.infer<typeof PolicyOverrides>;

// ---- Operations ----
//
// `roleGate` is documentation carried into the type system, NOT enforcement. It lets a
// lint test assert that every admin-gated call site sits inside a RequireAdmin subtree,
// and it lets the client attach the right context to a 403.

/** The resolved (tier-default ⊕ org-override) enforcement policy for an org. */
export const getOrgPolicy = {
  id: "getOrgPolicy",
  method: "GET",
  path: "/v1/orgs/{orgId}/policy",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: PolicyDocument,
  errors: [401, 403] as const,
} as const;
export type GetOrgPolicyResponse = z.infer<typeof getOrgPolicy.response>;

/** Replace the org's policy overrides with a validated document (admin-only). */
export const putOrgPolicy = {
  id: "putOrgPolicy",
  method: "PUT",
  path: "/v1/orgs/{orgId}/policy",
  roleGate: "admin",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: true,
  request: PolicyOverrides,
  successStatus: 200,
  response: PolicyDocument,
  errors: [400, 401, 403, 409, 503] as const,
} as const;
export type PutOrgPolicyRequest = z.infer<typeof putOrgPolicy.request>;
export type PutOrgPolicyResponse = z.infer<typeof putOrgPolicy.response>;

/** Deep-merge a partial override change into the org's policy (admin-only). */
export const patchOrgPolicy = {
  id: "patchOrgPolicy",
  method: "PATCH",
  path: "/v1/orgs/{orgId}/policy",
  roleGate: "admin",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: true,
  request: PolicyOverrides,
  successStatus: 200,
  response: PolicyDocument,
  errors: [400, 401, 403, 409, 503] as const,
} as const;
export type PatchOrgPolicyRequest = z.infer<typeof patchOrgPolicy.request>;
export type PatchOrgPolicyResponse = z.infer<typeof patchOrgPolicy.response>;

/** Drop all overrides back to the org's plan-tier defaults (admin-only). */
export const resetOrgPolicy = {
  id: "resetOrgPolicy",
  method: "POST",
  path: "/v1/orgs/{orgId}/policy:reset",
  roleGate: "admin",
  pathParams: ["orgId"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: PolicyDocument,
  errors: [401, 403, 503] as const,
} as const;
export type ResetOrgPolicyResponse = z.infer<typeof resetOrgPolicy.response>;

/** Paginated content-free change history for the org's policy. */
export const getPolicyAudit = {
  id: "getPolicyAudit",
  method: "GET",
  path: "/v1/orgs/{orgId}/policy/audit",
  roleGate: "any",
  pathParams: ["orgId"] as const,
  queryParams: {
    limit: { required: false },
    before: { required: false },
  } as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: z.array(PolicyAuditEntry),
  errors: [401, 403] as const,
} as const;
export type GetPolicyAuditResponse = z.infer<typeof getPolicyAudit.response>;

/** A prior resolved policy document, for diff/rollback in the admin UI. */
export const getPolicyVersion = {
  id: "getPolicyVersion",
  method: "GET",
  path: "/v1/orgs/{orgId}/policy/versions/{version}",
  roleGate: "any",
  pathParams: ["orgId", "version"] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: PolicyDocument,
  errors: [401, 403, 404] as const,
} as const;
export type GetPolicyVersionResponse = z.infer<typeof getPolicyVersion.response>;

export const policyOperations = { getOrgPolicy, putOrgPolicy, patchOrgPolicy, resetOrgPolicy, getPolicyAudit, getPolicyVersion } as const;
